import type { ReactElement, SyntheticEvent } from "react"
import React from "react"
import _t from "@core/i18n"
import { getFormattedPeriodInfo } from "@core/period-info"
import { PriceFormat } from "@core/types"
import type { ProductBundle } from "@onestore-graphql/Model"
import { get } from "lodash"
import ProductBundleMol from "@onestore/hel/dist/components/molecules/ProductBundleMol"
import type { Img } from "@onestore/hel/dist/typings/types"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import type { OptionMapping } from "@gatsby-plugin-marketplace/fragments/optionMapping"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { DomainExtensionsUpsell } from "~/fragments/domainExtensionsUpsell"
import type { DomainsPoolUpsell } from "~/fragments/domainsPoolUpsell"
import useLinkProperties from "~/hooks/useLinkProperties"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"

type ProductBundleMolItemProps = {
  img?: Img
  title: string
  price: string
  priceSuffix: string
  href: string
  hrefTitle: string
  onClick?: (event: SyntheticEvent) => void
}

type ProductBundleMolProps = {
  bundle: ProductBundle
  productItem: ProductBundleMolItemProps
  productSettings: OptionMapping
  upsell?:
    | (CloudBluePlan | DomainExtensionsUpsell | DomainsPoolUpsell)[]
    | undefined
}

export default function ProductBundleItem({
  bundle,
  productItem,
  productSettings,
  upsell,
}: ProductBundleMolProps): ReactElement<ProductBundleMolProps> | null {
  const { currentPriceType, isNettoPriceType } = usePriceTypeContext()

  const bundlePlanAlias = get(bundle, "flatData.plan[0].flatData.alias")

  const bundleItemUrl =
    get(bundle, "flatData.pages[0].flatData.url", bundle.flatData.url) || ""

  const bonusUrl = url.generateBonusUrl({
    p: get(productSettings, "cloudBluePlan[0].flatData.alias"),
    ax: getUpsellData(upsell),
    period: productSettings.period?.period,
    b: bundlePlanAlias,
    priceType: currentPriceType,
  })

  const bundleLinkProps = useLinkProperties(bonusUrl)

  const bundleItemLink = useLinkProperties(bundleItemUrl)

  if (!bundlePlanAlias) {
    return null
  }

  if (isEmpty(bundle.flatData.plan)) {
    return null
  }

  const bundleName: string = bundle.flatData.name || ""

  const bundleItem = {
    title: bundleName,
    img:
      bundle.flatData.logo && bundle.flatData.logo.length
        ? {
            src: bundle.flatData.logo[0].url,
            alt: bundleName,
            title: bundleName,
          }
        : undefined,
    href: bundleItemLink.href,
    hrefTitle: bundleName,
    onClick: bundleItemLink.onClick,
    price: getFormattedPeriodInfo(
      bundle.flatData.plan[0].flatData.periods,
      undefined,
      isNettoPriceType
        ? PriceFormat.REGISTER_NETTO_NO_PERIOD
        : PriceFormat.REGISTER_GROSS_NO_PERIOD
    ),
    priceSuffix: getFormattedPeriodInfo(
      bundle.flatData.plan[0].flatData.periods,
      undefined,
      isNettoPriceType
        ? PriceFormat.NETTO_WITH_PERIOD
        : PriceFormat.GROSS_WITH_PERIOD
    ),
    tooltip: bundle.flatData.tooltip,
  }

  return (
    <ProductBundleMol
      key={bundle.flatData.plan[0].flatData.alias}
      button={{
        text: _t("marketplace.chooseBundle"),
        title: _t("marketplace.chooseBundle"),
        ...bundleLinkProps,
        rel: "nofollow",
      }}
      items={[productItem, bundleItem]}
    />
  )
}
